import { dataEntryService } from "../services/data-entry.services";
import { consolidatedStore } from "../stores/consolidated.store";
import { disclosureStore } from "../stores/disclosure.store";
import { dataEntryFiltersStore } from "../stores/data-entry-filters.store";

// Stale-response guards. loadConsolidated's id is global; loadBreakdown's
// is per-disclosureId because each row's drill-down has its own lifecycle.
let consolidatedCallId = 0;
const breakdownCallIds = new Map();

// Derive a [yearFrom, yearTo] window from the shared year filter.
//   - "" (All Years) → full master-data range
//   - "2024"        → yearFrom = yearTo = 2024
//   - master-data empty → empty window (no API call)
const resolveYearRange = () => {
  const year = dataEntryFiltersStore.filters.year;
  if (year) return { yearFrom: Number(year), yearTo: Number(year) };
  const years = (disclosureStore.disclosures || []).flatMap(
    (d) => d.masterDataYears || [],
  );
  if (years.length === 0) return null;
  return { yearFrom: Math.min(...years), yearTo: Math.max(...years) };
};

export const loadBreakdown = async (rowKey, disclosureId, year) => {
  const cached = consolidatedStore.breakdownById.get(rowKey);
  if (cached && !cached.error) return;

  const baseRange = resolveYearRange();
  if (!baseRange) return;
  const range = year != null ? { yearFrom: year, yearTo: year } : baseRange;
  const { orgIds } = dataEntryFiltersStore.filters;

  const myId = (breakdownCallIds.get(rowKey) || 0) + 1;
  breakdownCallIds.set(rowKey, myId);

  consolidatedStore.setBreakdown(rowKey, { loading: true });
  try {
    const params = {
      yearFrom: range.yearFrom,
      yearTo: range.yearTo,
      disclosureId,
    };
    if (orgIds?.length) params.orgIds = orgIds.join(",");
    const data = await dataEntryService.listConsolidatedBreakdown(params);
    if (myId !== breakdownCallIds.get(rowKey)) return;
    consolidatedStore.setBreakdown(rowKey, {
      years: Array.isArray(data?.years) ? data.years : [],
      temporalAggregation: data?.temporalAggregation || "CUMULATIVE",
      items: Array.isArray(data?.items) ? data.items : [],
      ratioBreakdown: data?.ratioBreakdown || null,
      note: data?.note || null,
      // kind: "qualitative" | "parent_only" | null
      kind: data?.kind || null,
      inputType: data?.inputType || null,
      loading: false,
    });
  } catch (err) {
    if (myId !== breakdownCallIds.get(rowKey)) return;
    console.error("loadBreakdown failed:", err?.response?.data || err);
    consolidatedStore.setBreakdown(rowKey, {
      items: [],
      loading: false,
      error: true,
    });
  }
};

export const loadConsolidated = async () => {
  const myId = ++consolidatedCallId;
  consolidatedStore.clearBreakdownCache();

  const range = resolveYearRange();
  const { framework, category, orgIds } = dataEntryFiltersStore.filters;

  if (
    !range ||
    range.yearFrom < 1900 ||
    range.yearTo > 3000 ||
    range.yearFrom > range.yearTo
  ) {
    consolidatedStore.setItems([]);
    return;
  }

  const all = disclosureStore.disclosures || [];
  const matching = all.filter((d) => {
    if (framework && d.framework !== framework) return false;
    if (category && d.category !== category) return false;
    return true;
  });
  const disclosureIds = [
    ...new Set(matching.map((d) => d._id).filter(Boolean)),
  ];

  if (disclosureIds.length === 0) {
    consolidatedStore.setItems([]);
    return;
  }

  consolidatedStore.setLoading(true);
  try {
    const params = {
      yearFrom: range.yearFrom,
      yearTo: range.yearTo,
      disclosureIds,
    };
    if (orgIds?.length) params.orgIds = orgIds;

    const data = await dataEntryService.listConsolidated(params);
    if (myId !== consolidatedCallId) return;
    const items = Array.isArray(data?.items) ? data.items : [];

    // Enrich each item with disclosure metadata for display.
    const byId = new Map(all.map((d) => [d._id, d]));
    const enriched = items.map((item) => {
      const disc = byId.get(item.disclosureId) || {};
      const rowKey =
        item.year != null
          ? `${item.disclosureId}:${item.year}`
          : item.disclosureId;
      return {
        ...item,
        rowKey,
        disclosureCode: disc.code || null,
        disclosureTitle: disc.title || null,
        framework: disc.framework || null,
        category: disc.category || null,
        method: disc.consolidationMethod || item.method || null,
      };
    });

    consolidatedStore.setItems(enriched);
  } catch (err) {
    if (myId !== consolidatedCallId) return;
    console.error("loadConsolidated failed:", err?.response?.data || err);
    consolidatedStore.setItems([]);
  } finally {
    if (myId === consolidatedCallId) consolidatedStore.setLoading(false);
  }
};
